<template>
    <article
        :class="{'portfolio-item-card--actions-open': isDropdownOpen}"
        :data-testid="`portfolio-item-card-${portfolioItem.id}`"
        :role="'button'"
        class="portfolio-item-card"
        @click="emit('click')"
        @keydown.enter.prevent="emit('click')"
    >
        <div :class="{'portfolio-item-card__image-wrapper--external': isExternalThumbnail}" class="portfolio-item-card__image-wrapper">
            <img
                v-if="portfolioItem.thumbnail && isExternalThumbnail"
                :alt="portfolioItem.title"
                :src="portfolioItem.thumbnail"
                :title="portfolioItem.title"
                class="portfolio-item-card__image"
                loading="lazy"
            />
            <MaltImg
                v-else-if="portfolioItem.thumbnail"
                :alt="portfolioItem.title"
                :src="portfolioItem.thumbnail"
                :title="portfolioItem.title"
                class="portfolio-item-card__image"
                densities="1x"
                fit="cover"
                height="171"
                loading="lazy"
                width="304"
            />
            <div v-else class="portfolio-item-card__image portfolio-item-card__image--placeholder" />
            <div v-if="isEditionMode" class="portfolio-item-card__image-overlay" />
        </div>
        <div class="portfolio-item-card__meta">
            <div class="portfolio-item-card__meta-text">
                <VJoyText class="portfolio-item-card__title" preset="primary-450" tag-name="p">
                    {{ portfolioItem.title || t('profile.section.portfolio.item.untitled') }}
                </VJoyText>
                <VJoyText v-if="portfolioItem.description" class="portfolio-item-card__description" preset="primary-300" tag-name="p">
                    {{ portfolioItem.description || '' }}
                </VJoyText>
                <VJoyTag
                    v-if="hasAssociatedExperience"
                    class="portfolio-item-card__association"
                    data-testid="portfolio-item-card-associated-experience"
                    size="small"
                    variant="inactive"
                >
                    <VJoyIcon name="paperclip" />
                    {{ t('profile.section.portfolio.item.associated.experience') }}
                </VJoyTag>
            </div>
            <div v-if="isEditionMode" class="portfolio-item-card__actions" data-testid="portfolio-item-card-actions" @click.stop>
                <VJoyDropdown
                    close-on-select
                    direction="up"
                    dropdown-gap="0"
                    justify="right"
                    width="auto"
                    @dropdown:open="isDropdownOpen = true"
                    @dropdown:hide="isDropdownOpen = false"
                >
                    <template #dropdown-button>
                        <button
                            :aria-label="t('profile.section.portfolio.view.actions.aria.label')"
                            class="portfolio-item-card__actions-button"
                            type="button"
                        >
                            <VJoyIcon name="dots-vertical" size="xsmall" />
                        </button>
                    </template>
                    <template #dropdown-items>
                        <VJoyMenuItem
                            v-if="portfolioItem.published && canShare"
                            :label="t('profile.section.portfolio.share.label')"
                            data-testid="portfolio-item-action-share"
                            icon="social-share"
                            type="button"
                            @click="emit('share')"
                        />
                        <VJoyMenuItem
                            :label="t('general.edit')"
                            data-testid="portfolio-item-action-edit"
                            icon="pen"
                            type="button"
                            @click="emit('edit')"
                        />
                        <VJoyMenuItem
                            v-if="portfolioItem.published"
                            :label="t('profile.section.portfolio.item.actions.move.to.drafts')"
                            data-testid="portfolio-item-action-move-to-drafts"
                            icon="file"
                            type="button"
                            @click="emit('move-to-drafts')"
                        />
                        <VJoyMenuItem
                            :label="t('general.delete')"
                            class="portfolio-item-action--delete"
                            data-testid="portfolio-item-action-delete"
                            icon="trash"
                            type="button"
                            @click="confirmDeleteDialog?.show()"
                        />
                    </template>
                </VJoyDropdown>
            </div>
        </div>

        <VJoyDialog
            :id="`portfolio-item-delete-dialog-${portfolioItem.id}`"
            ref="confirmDeleteDialog"
            :cancel-text="t('general.cancel')"
            :confirm-text="t('profile.section.portfolio.sort.delete.dialog.confirm')"
            @dialog:confirm="onDeleteConfirm"
        >
            <template #dialog-header>
                {{ t('profile.section.portfolio.sort.delete.dialog.title') }}
            </template>
            <template #dialog-body>
                {{ t('profile.section.portfolio.sort.delete.dialog.description') }}
            </template>
        </VJoyDialog>
    </article>
</template>

<script lang="ts" setup>
    import {computed, ref, useTemplateRef} from 'vue';
    import {MaltImg} from '#components';
    import {useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyDialog, VJoyDropdown, VJoyIcon, VJoyMenuItem, VJoyTag, VJoyText} from '@maltjoy/core-vue';
    import type {PortfolioItemSummaryResource} from '@api';

    const props = defineProps<{portfolioItem: PortfolioItemSummaryResource; isEditionMode?: boolean; canShare?: boolean}>();
    const isExternalThumbnail = computed(() => !!props.portfolioItem.thumbnail && !props.portfolioItem.thumbnail.includes('dam.malt.com'));
    const isPortfolioExperienceLinkEnabled = useFeatureFlag('portfolio-experience-link');
    const hasAssociatedExperience = computed(() => isPortfolioExperienceLinkEnabled.value && !!props.portfolioItem.experienceId);

    const emit = defineEmits<{
        click: [];
        share: [];
        edit: [];
        'move-to-drafts': [];
        delete: [];
    }>();

    const {t} = useTranslation();
    const confirmDeleteDialog = useTemplateRef<InstanceType<typeof VJoyDialog> | null>('confirmDeleteDialog');
    const isDropdownOpen = ref(false);

    async function onDeleteConfirm() {
        emit('delete');
        await confirmDeleteDialog.value?.hide({fireHideEvent: false});
    }
</script>

<style lang="scss" scoped>
    .portfolio-item-card {
        width: 100%;
        overflow: visible;
        cursor: pointer;

        &__image-wrapper {
            position: relative;
            aspect-ratio: 16 / 9;
            border-radius: var(--joy-core-radius-3);
            overflow: hidden;
        }

        &__image {
            display: block;
            border-radius: var(--joy-core-radius-3);
            width: 100%;
            height: 100%;
            object-fit: cover;
            background: var(--joy-color-neutral-20);

            &--placeholder {
                aspect-ratio: 16 / 9;
                background: linear-gradient(135deg, var(--joy-color-neutral-20) 0%, var(--joy-color-neutral-30) 100%);
            }
        }

        &__image-overlay {
            position: absolute;
            inset: 0;
            background: var(--joy-color-neutral-60);
            opacity: 0;
            transition: opacity 0.2s ease;
            pointer-events: none;
            border-radius: var(--joy-core-radius-3);
        }

        &__meta {
            padding-top: var(--joy-core-spacing-2);
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-3);
        }

        &__meta-text {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        &__title {
            margin: 0;
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            transition: color 0.2s ease;
        }

        &__description {
            margin: 0;
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__association {
            align-self: flex-start;
            margin-top: var(--joy-core-spacing-1);
        }

        &__actions {
            flex-shrink: 0;
            opacity: 0;
            transition: opacity 0.2s ease;
            margin-right: var(--joy-core-spacing-2);
        }

        &__actions-button {
            display: flex;
            align-items: center;
            justify-content: center;
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
            border-radius: var(--joy-core-radius-2);

            &:hover {
                background: var(--joy-color-neutral-20);
            }
        }

        :deep(.joy-dropdown-list__items-slotted) {
            padding: 0;
        }

        :deep(.joy-menu-item__inner:hover) {
            color: var(--joy-color-secondary-50);
        }

        :deep(.portfolio-item-action--delete) {
            color: var(--joy-color-error-50);

            .joy-menu-item__icon-left {
                color: var(--joy-color-error-50);
            }

            &:hover {
                color: var(--joy-color-secondary-50);

                .joy-menu-item__icon-left {
                    color: var(--joy-color-secondary-50);
                }
            }
        }

        &:hover,
        &--actions-open {
            .portfolio-item-card__image-overlay {
                opacity: 0.15;
            }

            .portfolio-item-card__title {
                color: var(--joy-color-secondary-50);
            }

            .portfolio-item-card__actions {
                opacity: 1;
            }
        }
    }
</style>
